import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BookmarkPlus, Library } from 'lucide-react';
import { useIpeAuth } from '@/context/IpeAuthContext';
import { aiImageFissionPromptCaseAPI, fundingAuthAPI } from '@/utils/api';
import { showToast } from '@/utils/toast';
import ImageFissionAspectSizeField from './ImageFissionAspectSizeField';
import ImageFissionPromptCaseLibraryModal from '@/pages/ipe/workbench/components/ImageFissionPromptCaseLibraryModal';
import ImageFissionPromptCaseFormModal from '@/pages/ipe/workbench/modals/ImageFissionPromptCaseFormModal';
import IpeDeleteConfirmModal from '@/pages/ipe/workbench/modals/IpeDeleteConfirmModal';
import {
  DEFAULT_FISSION_ASPECT_PARAMS,
  IMAGE_FISSION_VARIATION_COUNT_OPTIONS,
} from '@/pages/ipe/workbench/constants/imageFissionStudioConfig';
import { resolveDefaultTitlePresetScope } from '@/pages/ipe/workbench/constants/titlePresetCatalog';
import { useIpeWorkspaceMode } from '@/pages/ipe/workbench/hooks/useIpeWorkspaceMode';
import { buildTeamOwnerNameMap } from '@/pages/ipe/workbench/utils/aiCopywritingTemplateLoader';
import { toIpeUserErrorMessage } from '@/pages/ipe/workbench/utils/errorHandler';
import {
  applyImageFissionCaseToStudioParams,
  mapImageFissionPromptCaseApiItem,
} from '@/pages/ipe/workbench/utils/imageFissionPromptCaseLoader';
import { isTeamSpaceEntryAvailable } from '@/pages/ipe/workbench/utils/accountCenterUtils';
import '@/styles/ipe/workbench/Img2ImgPage.css';
import '@/styles/ipe/workbench/ImageFissionPage.css';

function resolveAspectValue(p) {
  const defaults = DEFAULT_FISSION_ASPECT_PARAMS;
  const aspectRatio = String(p?.aspectRatio || defaults.aspectRatio);
  if (aspectRatio === 'original') {
    return { aspectRatio: 'original', outputWidth: 0, outputHeight: 0 };
  }
  const outputWidth = Math.max(
    1,
    Math.floor(Number(p?.outputWidth ?? p?.customAspectWidth ?? defaults.outputWidth) || 0),
  );
  const outputHeight = Math.max(
    1,
    Math.floor(Number(p?.outputHeight ?? p?.customAspectHeight ?? defaults.outputHeight) || 0),
  );
  return { aspectRatio, outputWidth, outputHeight };
}

/** 与 ImageFissionPage 侧栏一致：比例尺寸、选用/新增模板、仅裂变图案、生成数量 */
export default function ImageFissionStepParams({ p, onChange, readOnly = false }) {
  const { t } = useTranslation('workbench');
  const { user } = useIpeAuth();
  const workspaceMode = useIpeWorkspaceMode();
  const currentUserId = user?.id != null ? String(user.id) : (user?._id != null ? String(user._id) : '');

  const [canAccessTeamPresets, setCanAccessTeamPresets] = useState(false);
  const [teamOwnerNameByUserId, setTeamOwnerNameByUserId] = useState({});
  const [caseListNonce, setCaseListNonce] = useState(0);
  const [caseLibraryOpen, setCaseLibraryOpen] = useState(false);
  const [caseLibraryScope, setCaseLibraryScope] = useState(() => (
    resolveDefaultTitlePresetScope(workspaceMode, false)
  ));
  const [caseSelectedId, setCaseSelectedId] = useState('');
  const [caseFormOpen, setCaseFormOpen] = useState(false);
  const [caseFormMode, setCaseFormMode] = useState('create');
  const [caseFormScope, setCaseFormScope] = useState('personal');
  const [caseFormEditing, setCaseFormEditing] = useState(null);
  const [caseFormDefaults, setCaseFormDefaults] = useState(null);
  const [caseFormKey, setCaseFormKey] = useState(0);
  const [caseFormSaving, setCaseFormSaving] = useState(false);
  const [caseDeleteTarget, setCaseDeleteTarget] = useState(null);
  const [caseDeleting, setCaseDeleting] = useState(false);

  const aspectValue = useMemo(() => resolveAspectValue(p), [p]);
  const patternOnly = p.patternOnly === true;
  const variationCount = IMAGE_FISSION_VARIATION_COUNT_OPTIONS.includes(Number(p.variationCount))
    ? Number(p.variationCount)
    : 1;

  useEffect(() => {
    let cancelled = false;
    fundingAuthAPI.getAccountCenter()
      .then(async (center) => {
        if (cancelled) return;
        const available = isTeamSpaceEntryAvailable(center);
        setCanAccessTeamPresets(available);
        setCaseLibraryScope((prev) => {
          const preferred = resolveDefaultTitlePresetScope(workspaceMode, available);
          if (prev === 'team' && !available) return preferred;
          return prev || preferred;
        });
        if (!available) {
          setTeamOwnerNameByUserId({});
          return;
        }
        try {
          const overview = await fundingAuthAPI.getTeamOverview();
          if (!cancelled) {
            setTeamOwnerNameByUserId(buildTeamOwnerNameMap(overview?.members));
          }
        } catch {
          if (!cancelled) setTeamOwnerNameByUserId({});
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCanAccessTeamPresets(false);
          setTeamOwnerNameByUserId({});
        }
      });
    return () => {
      cancelled = true;
    };
  }, [workspaceMode]);

  const bumpCaseList = useCallback(() => {
    setCaseListNonce((n) => n + 1);
  }, []);

  const handleAspectChange = (patch) => {
    onChange({
      ...patch,
      customAspectWidth: undefined,
      customAspectHeight: undefined,
    });
  };

  const openCaseFormCreate = useCallback((scope, options = {}) => {
    setCaseLibraryOpen(false);
    setCaseFormMode('create');
    setCaseFormScope(scope === 'team' ? 'team' : 'personal');
    setCaseFormEditing(null);
    setCaseFormDefaults(options.prefillFromParams
      ? {
        aspectRatio: aspectValue.aspectRatio,
        outputWidth: aspectValue.outputWidth,
        outputHeight: aspectValue.outputHeight,
        patternOnly,
        variationCount,
      }
      : null);
    setCaseFormKey((k) => k + 1);
    setCaseFormOpen(true);
  }, [aspectValue, patternOnly, variationCount]);

  const openCaseFormEdit = useCallback((item) => {
    if (!item?.canEdit) {
      showToast(t('studioToast.noPermEdit'), 'warning');
      return;
    }
    setCaseLibraryOpen(false);
    setCaseFormMode('edit');
    setCaseFormScope(item.scope === 'team' ? 'team' : 'personal');
    setCaseFormEditing(item);
    setCaseFormDefaults(null);
    setCaseFormKey((k) => k + 1);
    setCaseFormOpen(true);
  }, [t]);

  const openCaseDelete = useCallback((item) => {
    if (!item?.canDelete) {
      showToast(t('studioToast.noPermDelete'), 'warning');
      return;
    }
    setCaseDeleteTarget(item);
  }, [t]);

  const handleCaseDeleteConfirm = useCallback(async () => {
    const target = caseDeleteTarget;
    if (!target?.id) return;
    setCaseDeleting(true);
    try {
      await aiImageFissionPromptCaseAPI.delete(target.id);
      showToast(t('studioToast.templateDeleted'), 'success');
      setCaseDeleteTarget(null);
      if (caseFormEditing && String(caseFormEditing.id) === String(target.id)) {
        setCaseFormOpen(false);
        setCaseFormEditing(null);
      }
      if (String(caseSelectedId) === String(target.id)) {
        setCaseSelectedId('');
      }
      bumpCaseList();
    } catch (e) {
      showToast(toIpeUserErrorMessage(e, t('studioToast.templateDeleteFailed')), 'error');
    } finally {
      setCaseDeleting(false);
    }
  }, [caseDeleteTarget, caseFormEditing, caseSelectedId, bumpCaseList, t]);

  const handleCaseFormSubmit = useCallback(async (payload) => {
    setCaseFormSaving(true);
    try {
      let savedCase = null;
      if (caseFormMode === 'edit' && caseFormEditing?.id) {
        const { caseScope: _scope, ...updateBody } = payload;
        const res = await aiImageFissionPromptCaseAPI.update(caseFormEditing.id, updateBody);
        savedCase = mapImageFissionPromptCaseApiItem(res?.case);
        showToast(t('studioToast.templateSaved'), 'success');
      } else {
        const res = await aiImageFissionPromptCaseAPI.create(payload);
        savedCase = mapImageFissionPromptCaseApiItem(res?.case);
        showToast(t('studioToast.templateCreated'), 'success');
      }
      if (savedCase) {
        setCaseSelectedId(String(savedCase.id));
        const patch = applyImageFissionCaseToStudioParams(savedCase);
        if (patch) onChange(patch);
      }
      setCaseFormOpen(false);
      setCaseFormEditing(null);
      setCaseFormDefaults(null);
      if (payload.caseScope === 'personal' || payload.caseScope === 'team') {
        setCaseLibraryScope(payload.caseScope);
      }
      bumpCaseList();
    } catch (e) {
      showToast(toIpeUserErrorMessage(e, t('studioToast.templateSaveFailed')), 'error');
    } finally {
      setCaseFormSaving(false);
    }
  }, [caseFormMode, caseFormEditing, onChange, bumpCaseList, t]);

  const applyCase = useCallback((item) => {
    if (!item) return;
    setCaseSelectedId(String(item.id || ''));
    const patch = applyImageFissionCaseToStudioParams(item);
    if (patch) onChange(patch);
  }, [onChange]);

  const openLibrary = () => {
    if (caseLibraryOpen) {
      setCaseLibraryOpen(false);
      return;
    }
    if (caseFormOpen) setCaseFormOpen(false);
    setCaseLibraryScope(
      resolveDefaultTitlePresetScope(workspaceMode, canAccessTeamPresets),
    );
    setCaseLibraryOpen(true);
  };

  const openCreate = () => {
    if (caseFormOpen && caseFormMode === 'create') {
      setCaseFormOpen(false);
      return;
    }
    openCaseFormCreate(
      resolveDefaultTitlePresetScope(workspaceMode, canAccessTeamPresets) === 'team'
        ? 'team'
        : 'personal',
      { prefillFromParams: true },
    );
  };

  return (
    <div className="img2img-page wf-studio-params-root wf-editor-img-fission img-fission-page">
      <section className="img2img-studio__section img-fission-page__aspect-section">
        <div className="img2img-studio__field-row">
          <span className="img2img-studio__field-label img-fission-page__aspect-title">{t('workflowParams.aspect')}</span>
          {!readOnly ? (
            <div className="studio-case-actions">
              <button
                type="button"
                className="studio-case-btn"
                onClick={openLibrary}
              >
                <Library size={14} aria-hidden="true" />
                {t('workflowParams.pickTemplate')}
              </button>
              <button
                type="button"
                className="studio-case-btn"
                onClick={openCreate}
              >
                <BookmarkPlus size={14} aria-hidden="true" />
                {t('workflowParams.newTemplate')}
              </button>
            </div>
          ) : null}
        </div>
        <ImageFissionAspectSizeField
          value={aspectValue}
          onChange={handleAspectChange}
          readOnly={readOnly}
        />
      </section>

      <section className="img2img-studio__section">
        <div className="img2img-studio__batch-row">
          <div className="img2img-studio__batch-copy">
            <span className="img2img-studio__field-label">{t('workflowParams.fissionOnly')}</span>
          </div>
          <label className="img2img-studio__toggle">
            <input
              type="checkbox"
              checked={patternOnly}
              onChange={(e) => onChange({ patternOnly: e.target.checked })}
              disabled={readOnly}
              aria-label={t('workflowParams.fissionOnly')}
            />
            <span className="img2img-studio__toggle-track" aria-hidden="true" />
          </label>
        </div>
      </section>

      <section className="img2img-studio__section">
        <div className="img2img-studio__field-row">
          <span className="img2img-studio__field-label">{t('workflowParams.qty')}</span>
          <span className="img2img-studio__field-hint">{t('workflowParams.qtyHint')}</span>
        </div>
        <div className="img2img-studio__qty-row" role="group" aria-label={t('workflowParams.qty')}>
          {IMAGE_FISSION_VARIATION_COUNT_OPTIONS.map((n) => (
            <button
              key={n}
              type="button"
              className={`img2img-studio__qty-btn${variationCount === n ? ' is-active' : ''}`}
              onClick={() => onChange({ variationCount: n })}
              disabled={readOnly}
              aria-pressed={variationCount === n}
            >
              {n}
            </button>
          ))}
        </div>
      </section>

      <p className="wf-editor-params__muted wf-editor-fission__footnote">
        {t('workflowParams.fissionRuntime')}
      </p>

      <ImageFissionPromptCaseLibraryModal
        isOpen={caseLibraryOpen}
        onClose={() => setCaseLibraryOpen(false)}
        selectedId={caseSelectedId}
        initialScope={caseLibraryScope}
        canAccessTeamPresets={canAccessTeamPresets}
        currentUserId={currentUserId}
        ownerNameByUserId={teamOwnerNameByUserId}
        refreshNonce={caseListNonce}
        escapeDisabled={caseFormOpen || Boolean(caseDeleteTarget)}
        centered
        onSelect={applyCase}
        onRequestCreate={(scope) => {
          setCaseLibraryOpen(false);
          openCaseFormCreate(scope, { prefillFromParams: false });
        }}
        onRequestEdit={(item) => {
          setCaseLibraryOpen(false);
          openCaseFormEdit(item);
        }}
        onRequestDelete={openCaseDelete}
      />

      <ImageFissionPromptCaseFormModal
        key={`fission-case-form-${caseFormKey}`}
        isOpen={caseFormOpen}
        mode={caseFormMode}
        initialScope={caseFormScope}
        caseItem={caseFormEditing}
        defaults={caseFormMode === 'create' ? caseFormDefaults : null}
        canAccessTeamPresets={canAccessTeamPresets}
        saving={caseFormSaving}
        centered
        onClose={() => {
          if (caseFormSaving) return;
          setCaseFormOpen(false);
          setCaseFormEditing(null);
          setCaseFormDefaults(null);
        }}
        onSubmit={handleCaseFormSubmit}
      />

      <IpeDeleteConfirmModal
        open={Boolean(caseDeleteTarget)}
        title={t('studioChrome.deleteTemplate')}
        confirming={caseDeleting}
        confirmDisabled={caseDeleting}
        onCancel={() => {
          if (caseDeleting) return;
          setCaseDeleteTarget(null);
        }}
        onConfirm={handleCaseDeleteConfirm}
      >
        {caseDeleteTarget
          ? t('studioChrome.deleteTemplateConfirm', { name: caseDeleteTarget.name })
          : ''}
      </IpeDeleteConfirmModal>
    </div>
  );
}
